"use client";

import { UserCard } from "@/components/account/profile/user-card";
import { type SortOption, SortSelector } from "@/components/blog/sort-selector";
import { ErrorBox } from "@/components/ui/ErrorBox";
import { LoadingBox } from "@/components/ui/LoadingBox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { EnrichedProfile } from "@/lib/supabase/types";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { HeartCrackIcon, MessageSquare, Tag, ThumbsUp } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { type ProfileActivity, getProfileActivity } from "./profile-activity";
import ActivityOptions, { sortOptions } from "./profile-activity-options";

interface ProfileActivitiesProps {
	profile: EnrichedProfile | null;
}

export default function ProfileActivities({ profile }: ProfileActivitiesProps) {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [profileActivities, setProfileActivities] = useState<ProfileActivity[]>(
		[],
	);
	const [sort, setSort] = useState<"recent" | "oldest">("recent");
	const [type, setType] = useState<string[]>([
		"comment",
		"reaction",
		"bookmark",
	]);

	useEffect(() => {
		let isMounted = true;

		const fetchActivities = async () => {
			if (!profile?.id) {
				if (isMounted) {
					setError("User profile not found");
					setLoading(false);
				}
				return;
			}

			try {
				setLoading(true);
				setError(null);

				const { activities, error } = await getProfileActivity({
					userId: profile.id,
					sort,
					type,
				});

				if (!isMounted) return;

				if (error) {
					throw new Error(error || "Failed to load activities");
				}

				setProfileActivities(activities);
			} catch (err) {
				console.error("Error fetching activities:", err);
				if (isMounted) {
					setError(
						err instanceof Error ? err.message : "An unknown error occurred",
					);
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchActivities();

		return () => {
			isMounted = false;
		};
	}, [profile?.id, sort, type]);

	let currentDate = "";
	const formatDate = (date: string) => {
		return format(date, "EEEE, dd MMMM").toUpperCase();
	};
	const formatTime = (date: string) => {
		return format(date, "hh:mm a").toUpperCase();
	};

	const handleSortChange = (option: SortOption) => {
		setSort(option.value as "recent" | "oldest");
	};

	if (loading) {
		return <LoadingBox message="Loading profile activities" />;
	}

	if (error) {
		return <ErrorBox error="Error loading profile activities" />;
	}

	return (
		<div className="w-full flex flex-col gap-2 mb-10">
			{/* Header */}
			<div className="mt-6 w-full flex flex-row justify-between gap-4 items-center">
				<h1 className="text-primary text-2xl font-semibold md:text-3xl">
					Activity Log
				</h1>
				{profileActivities.length > 0 && (
					<div className="flex flex-row gap-2 items-center justify-between">
						<ToggleGroup
							type="multiple"
							variant="outline"
							value={type}
							onValueChange={setType}
						>
							<ToggleGroupItem
								value="comment"
								aria-label="Toggle comments"
								variant="default"
							>
								<MessageSquare />
							</ToggleGroupItem>
							<ToggleGroupItem
								value="reaction"
								aria-label="Toggle reactions"
								variant="default"
							>
								<ThumbsUp />
							</ToggleGroupItem>
							<ToggleGroupItem
								value="bookmark"
								aria-label="Toggle bookmarks"
								variant="default"
							>
								<Tag />
							</ToggleGroupItem>
						</ToggleGroup>
						<SortSelector
							options={sortOptions}
							onChange={handleSortChange}
							className="hidden sm:flex"
						/>
					</div>
				)}
			</div>

			{/* Activities */}
			<div className="space-y-1 md:space-y-2">
				{profileActivities.length < 1 ? (
					<div className="w-full items-center flex flex-col gap-2 justify-center my-4">
						<HeartCrackIcon className="size-12 text-muted-foreground" />
						<p>No activities found</p>
					</div>
				) : (
					profileActivities.map((item, index) => {
						const showDate = currentDate !== formatDate(item.created_at ?? "");
						if (showDate && item.created_at) {
							currentDate = formatDate(item.created_at ?? "");
						}

						return (
							<div key={item.id}>
								{/* Date Header */}
								{showDate && (
									<div
										className={cn(
											"text-muted-foreground mb-6 text-xs font-medium tracking-wide uppercase md:text-sm",
											showDate && index !== 0 ? "mt-6" : "",
										)}
									>
										{formatDate(item.created_at ?? "")}
									</div>
								)}

								{/* Activity Item */}
								<div className="relative flex gap-2 md:gap-3 hover:bg-primary/10 transition-colors rounded-xl p-2 items-center">
									{/* Timeline Line */}
									<div className="bg-border absolute top-10 bottom-0 left-3 w-px md:top-12 md:left-4" />

									{/* Content */}
									<div className="min-w-0 flex-1">
										<div className="flex flex-col">
											<div className="w-full flex flex-row gap-1 justify-between sm:items-center sm:gap-2">
												<div className="relative z-10 flex gap-2">
													<div className="flex items-center justify-center rounded-full">
														{getActivityIcon(item.type)}
													</div>
													{/* <span className="text-primary/64">
                                                    #{item.type}
                                                </span> */}
												</div>
												<div className="flex items-center gap-2">
													<span className="text-muted-foreground text-xs md:text-sm">
														{formatTime(item.created_at ?? "")}
													</span>
													<ActivityOptions activity={item} profile={profile} />
												</div>
											</div>
										</div>

										<div className="ml-3 md:ml-4">
											{/* Activity */}
											<div className="flex flex-wrap items-center gap-1">
												{profile && <UserCard profile={profile} />}
												<span className="text-primary/64">{item.action}</span>
												{item.link && (
													<Link
														href={item.link.url}
														className="px-1 font-bold transition-colors hover:text-primary hover:underline hover:underline-offset-4"
													>
														{item.link.title}
													</Link>
												)}
											</div>

											{/* Comment */}
											{item.comment && (
												<div className="bg-muted text-muted-foreground mt-2 rounded-lg p-2 text-xs leading-relaxed md:mt-3 md:p-3 md:text-sm">
													{item.comment}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
}

const getActivityIcon = (type: string) => {
	switch (type) {
		case "comment":
			return <MessageSquare className="text-primary w-6 h-6" />;
		case "reaction":
			return <ThumbsUp className="text-primary w-6 h-6" />;
		case "bookmark":
			return <Tag className="text-primary w-6 h-6" />;
		default:
			return null;
	}
};
